import { AdminReportService } from "../../../services/admin-report.service.js";

export const ReportsTab = async (container) => {
    // State
    const state = {
        filter: 'all', // all, enom, general
        lastDoc: null,
        reports: [],
        isLoading: false,
        hasMore: true
    };

    // 1. Initial Frame
    container.innerHTML = `
        <div class="animate-fade-in space-y-6">
            <div class="flex items-center justify-between">
                <h2 class="text-2xl font-black text-gray-900 flex items-center gap-2">
                    <span class="material-symbols-outlined text-red-600">gavel</span> 
                    Report Center
                </h2>
                <div class="flex bg-gray-100 p-1 rounded-lg">
                    <button class="px-4 py-1.5 rounded-md text-sm font-bold transition-all shadow-sm bg-white text-gray-800" data-filter="all">전체 (All)</button>
                    <button class="px-4 py-1.5 rounded-md text-sm font-bold text-gray-500 hover:text-gray-700 transition-all" data-filter="enom">이놈 (ENOM)</button>
                    <button class="px-4 py-1.5 rounded-md text-sm font-bold text-gray-500 hover:text-gray-700 transition-all" data-filter="general">일반 신고</button>
                </div>
            </div>

            <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <!-- Active Queue -->
                <div class="lg:col-span-2 space-y-4">
                    <div class="flex items-center justify-between">
                        <h3 class="font-bold text-gray-800 flex items-center gap-2">
                            <span class="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse"></span> 
                            접수된 신고 (Active Queue)
                        </h3>
                        <button id="btn-refresh" class="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1">
                            <span class="material-symbols-outlined text-sm">refresh</span> 새로고침
                        </button>
                    </div>
                    
                    <div id="active-reports-list" class="space-y-4 min-h-[400px]">
                        <!-- List Items -->
                    </div>
                    
                    <button id="btn-load-more" class="w-full py-3 text-sm font-bold text-gray-500 hover:text-gray-700 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors hidden">
                        더 보기 (Load More)
                    </button>
                </div>

                <!-- History Side -->
                <div class="space-y-4">
                    <h3 class="font-bold text-gray-500 flex items-center gap-2">
                        <span class="material-symbols-outlined text-sm">history</span> 
                        최근 심판 내역 (History)
                    </h3>
                    <div id="resolved-reports-list" class="space-y-3 opacity-80">
                        <div class="h-20 bg-gray-50 rounded-xl animate-pulse"></div>
                    </div>
                </div>
            </div>
        </div>
    `;

    // Elements
    const listEl = container.querySelector('#active-reports-list');
    const loadMoreBtn = container.querySelector('#btn-load-more');
    const refreshBtn = container.querySelector('#btn-refresh');
    const historyList = container.querySelector('#resolved-reports-list');
    const filterBtns = container.querySelectorAll('[data-filter]');

    // --- Logic ---

    const renderList = () => {
        if (state.reports.length === 0 && !state.isLoading) {
            listEl.innerHTML = `
                <div class="flex flex-col items-center justify-center py-20 bg-white rounded-2xl border border-dashed border-gray-200">
                    <div class="w-16 h-16 bg-green-50 text-green-500 rounded-full flex items-center justify-center mb-4">
                        <span class="material-symbols-outlined text-3xl">check</span>
                    </div>
                    <p class="font-bold text-gray-800">클린합니다!</p>
                    <p class="text-sm text-gray-400">접수된 신고가 없습니다.</p>
                </div>
            `;
            loadMoreBtn.classList.add('hidden');
            return;
        }

        const html = state.reports.map(report => {
            const date = report.createdAt ? new Date(report.createdAt.seconds * 1000).toLocaleString() : '-';
            const isEnom = report.source === 'enom';

            // [VIEW A] ENOM / User-Targeting Report (Card View)
            if (isEnom || report.targetUserId) {
                return `
                <div class="bg-white p-5 rounded-xl shadow-sm border border-red-100 relative group transition-all hover:shadow-md animate-slide-up">
                    <div class="flex items-start justify-between mb-3">
                        <div class="flex items-center gap-2">
                            <span class="bg-red-600 text-white px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide">
                                관리자 심판 (ENOM)
                            </span>
                            <span class="text-xs text-gray-400 font-mono">${date}</span>
                        </div>
                    </div>

                    <div class="flex gap-4">
                        <div class="flex-1">
                            <p class="text-xs text-gray-500 mb-1">대상 사용자 (Target)</p>
                            <div class="flex items-center gap-2 mb-3">
                                <span class="material-symbols-outlined text-gray-400 text-sm">person</span>
                                <span class="font-bold text-gray-800 bg-gray-50 px-1.5 rounded cursor-pointer select-all" title="ID 복사">${report.targetUserId}</span>
                            </div>
                        </div>
                        ${report.sourceUserId ? `
                        <div class="flex-1 border-l border-gray-100 pl-4">
                            <p class="text-xs text-gray-500 mb-1">신고자 (Reporter)</p>
                            <div class="flex items-center gap-2 mb-3">
                                <span class="font-medium text-gray-700 text-sm">${report.sourceUserName || 'Unknown'}</span>
                            </div>
                        </div>
                        ` : ''}
                    </div>

                    <div class="bg-gray-50 p-3 rounded-lg border border-gray-100 mb-4">
                        <p class="text-sm text-gray-700 italic">"${report.description || report.reason || '내용 없음'}"</p>
                    </div>
                    
                    <div class="grid grid-cols-3 gap-2 mt-4 pt-4 border-t border-gray-100">
                        <button class="bg-white border border-gray-200 text-gray-500 py-2 rounded-lg text-xs font-bold hover:bg-gray-50 transition-colors btn-action" 
                            data-action="dismiss" data-id="${report.id}" data-uid="${report.targetUserId}">반려 (Dismiss)</button>
                        <button class="bg-orange-50 text-orange-600 py-2 rounded-lg text-xs font-bold hover:bg-orange-100 transition-colors btn-action" 
                            data-action="suspend" data-id="${report.id}" data-uid="${report.targetUserId}">3일 정지 (Suspend)</button>
                        <button class="bg-red-600 text-white py-2 rounded-lg text-xs font-bold hover:bg-red-700 transition-colors shadow-sm btn-action" 
                            data-action="ban" data-id="${report.id}" data-uid="${report.targetUserId}">영구 제명 (BAN)</button>
                    </div>
                </div>
                `;
            }

            // [VIEW B] GENERAL / Content QA Report (List/Table Row View)
            // Focused on URL and Content
            else {
                return `
                <div class="bg-white p-4 rounded-lg shadow-sm border border-gray-200 hover:border-blue-300 transition-all flex flex-col md:flex-row gap-4 items-start md:items-center animate-slide-up">
                    <!-- Icon/Badge -->
                    <div class="shrink-0 flex flex-col items-center gap-1 min-w-[60px]">
                        <span class="material-symbols-outlined text-gray-400">article</span>
                        <span class="text-[10px] font-bold text-gray-500 uppercase">${report.category || report.reason || 'GENERAL'}</span>
                    </div>

                    <!-- Content -->
                    <div class="flex-1 min-w-0 w-full space-y-1">
                        <div class="text-sm text-gray-800 line-clamp-2 md:line-clamp-1 font-medium" title="${report.content || report.description}">
                            ${report.content || report.description || '내용 없음'}
                        </div>
                        
                        <div class="flex items-center gap-3 text-xs text-gray-500">
                            <span>${date}</span>
                            ${report.url ? `
                                <span class="w-px h-3 bg-gray-300"></span>
                                <a href="${report.url}" target="_blank" class="text-blue-500 hover:underline flex items-center gap-1 max-w-[200px] truncate">
                                    <span class="material-symbols-outlined text-[10px]">link</span> ${report.url}
                                </a>
                            ` : ''}
                        </div>
                        
                        <!-- Extra Metadata line if needed -->
                        ${report.userId || report.userEmail ? `
                            <div class="text-[10px] text-gray-400 pt-1">
                                By: ${report.userEmail || report.userId || 'Anonymous'}
                            </div>
                        ` : ''}
                    </div>

                    <!-- Actions -->
                    <div class="shrink-0 flex items-center gap-2 w-full md:w-auto mt-2 md:mt-0 justify-end">
                        <button class="px-3 py-1.5 rounded border border-gray-200 text-gray-500 text-xs font-bold hover:bg-gray-50 btn-action" 
                            data-action="dismiss" data-id="${report.id}" data-uid="null">
                            삭제/반려
                        </button>
                        <button class="px-3 py-1.5 rounded bg-blue-50 text-blue-600 border border-blue-100 text-xs font-bold hover:bg-blue-100 btn-action" 
                            data-action="resolve" data-id="${report.id}" data-uid="null">
                            확인 완료
                        </button>
                    </div>
                </div>
                `;
            }
        }).join('');

        listEl.innerHTML = html;

        // Re-bind actions
        listEl.querySelectorAll('.btn-action').forEach(btn => {
            btn.onclick = (e) => handleAction(e.target.dataset.action, e.target.dataset.id, e.target.dataset.uid, container);
        });

        if (state.hasMore) loadMoreBtn.classList.remove('hidden');
        else loadMoreBtn.classList.add('hidden');
    };

    const fetchReports = async (reset = false) => {
        if (state.isLoading) return;
        state.isLoading = true;

        if (reset) {
            state.lastDoc = null;
            state.reports = [];
            listEl.innerHTML = `<div class="flex justify-center py-10"><span class="loader-spin w-8 h-8 border-4 border-gray-200 border-t-indigo-500 rounded-full animate-spin"></span></div>`;
        } else {
            loadMoreBtn.innerText = '로딩 중...';
        }

        try {
            const result = await AdminReportService.fetchActiveReports(state.lastDoc, 10, state.filter);

            state.reports = reset ? result.data : [...state.reports, ...result.data];
            state.lastDoc = result.lastDoc;
            state.hasMore = result.data.length === 10; // Simple check

        } catch (e) {
            console.error(e);
            if (reset) listEl.innerHTML = `<div class="p-4 bg-red-50 text-red-500 rounded-lg">Error: ${e.message}</div>`;
        } finally {
            state.isLoading = false;
            loadMoreBtn.innerText = '더 보기 (Load More)';
            renderList();
        }
    };

    const fetchHistory = async () => {
        try {
            const history = await AdminReportService.fetchResolvedHistory();
            if (history.length === 0) {
                historyList.innerHTML = `<div class="text-center text-gray-300 py-4 text-xs">기록이 없습니다.</div>`;
            } else {
                historyList.innerHTML = history.map(report => {
                    let statusColor = 'text-gray-500';
                    let statusText = report.resolution;

                    if (report.resolution === 'ban') { statusColor = 'text-red-600 font-bold'; statusText = 'BAN'; }
                    if (report.resolution === 'suspend') { statusColor = 'text-orange-500 font-bold'; statusText = '3d Stop'; }
                    if (report.resolution === 'dismiss') { statusColor = 'text-green-600'; statusText = 'Dismiss'; }

                    return `
                    <div class="flex items-center justify-between p-3 bg-white border border-gray-100 rounded-lg text-xs shadow-sm">
                        <div class="flex flex-col">
                            <span class="font-bold text-gray-700">${report.targetUserId}</span>
                            <span class="text-[10px] text-gray-400 truncate max-w-[150px]">${report.adminNote || report.reason}</span>
                        </div>
                        <span class="uppercase ${statusColor}">${statusText}</span>
                    </div>
                `;
                }).join('');
            }
        } catch (e) {
            historyList.innerHTML = `<div class="text-red-400 text-xs">Error loading history</div>`;
        }
    };

    // Event Listeners
    filterBtns.forEach(btn => {
        btn.onclick = () => {
            // UI Toggle
            filterBtns.forEach(b => {
                b.className = "px-4 py-1.5 rounded-md text-sm font-bold text-gray-500 hover:text-gray-700 transition-all";
            });
            btn.className = "px-4 py-1.5 rounded-md text-sm font-bold transition-all shadow-sm bg-white text-gray-800";

            state.filter = btn.dataset.filter;
            fetchReports(true);
        };
    });

    loadMoreBtn.onclick = () => fetchReports(false);
    refreshBtn.onclick = () => {
        fetchReports(true);
        fetchHistory();
    };

    // Initial Load
    fetchReports(true);
    fetchHistory();
};

// Reuse Action Handler
const handleAction = async (action, reportId, targetUid, container) => {
    // ... same as before, but ensure we refresh cleanly
    const actionText = action.toUpperCase();

    // Using global Modal if available or confirm
    // Assuming window.Modal exists from previous context
    const note = await window.Modal.prompt(
        `[${actionText}] 결정을 내리시겠습니까?\n사유/노트를 입력하세요:`,
        "",
        "심판 집행"
    );

    if (note !== null) {
        try {
            await AdminReportService.executeJudgement(reportId, targetUid, action, note || "No details");
            window.Modal.toast(`Status Updated: [${actionText}]`);
            // Trigger refresh on the tab
            // Since we don't have direct reference to `state` inside this decoupled function easily without passing it,
            // we can just re-click the refresh button or re-run the module logic if we structured it as a class.
            // For now, let's just cheat and re-click the refresh button if it exists
            const refreshBtn = container.querySelector('#btn-refresh');
            if (refreshBtn) refreshBtn.click();
        } catch (e) {
            window.Modal.alert("Error: " + e.message);
        }
    }
};
